// Plan-based feature gating.
//
// Major functionality is split into named features, each turned on/off by the active plan.
// There is no billing/subscription model yet, so the active plan is resolved from a
// configurable source (runtimeConfig.public.plan, env NUXT_PUBLIC_PLAN) and defaults to the
// top tier — everything is on today. When a real plan/subscription lands, only `resolvePlan`
// needs to change (e.g. read org.plan); the FEATURES matrix and all `has()` call sites stay.

import { type FeatureKey, type PlanId, PLAN_FEATURES, toPlan } from '#shared/plan'

export type { FeatureKey, PlanId }

/**
 * The active plan. No subscription model yet → configurable, defaults to the top tier so all
 * features are on. Swap this for the real source (org/subscription) without touching callers.
 */
export function resolvePlan(): PlanId {
  return toPlan(useRuntimeConfig().public?.plan)
}

export function useFeatures() {
  const plan = computed<PlanId>(() => resolvePlan())
  const enabled = computed<Set<FeatureKey>>(() => new Set(PLAN_FEATURES[plan.value] ?? []))

  /** Whether the active plan includes a feature. */
  function has(feature: FeatureKey): boolean {
    return enabled.value.has(feature)
  }

  return { plan, has, enabled }
}
